/**
 * Custom Tag Rule constants (ENG-5607).
 *
 * Shared between metadata-svc (rule storage + evaluator) and apps/valet
 * (Settings > Tagging UI). Source of truth for: the v1 trigger list,
 * operator vocabulary used by the condition builder, and the variable
 * catalog rendered as chips in the Tag Templates step.
 */

// Re-export the Prisma-generated TagScope enum so callers get a
// single canonical declaration. The previous const-object + derived
// type collided with the typegen enum in
// `libs/shared/types/database/metadata-svc.ts` — same name, two
// files, silent comparison risk.
export { TagScope } from '@monolog/shared/types/database/metadata-svc';

export const CustomTagOperator = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  IS_ONE_OF: 'is_one_of',
  IS_NOT_ONE_OF: 'is_not_one_of',
  CONTAINS: 'contains',
  STARTS_WITH: 'starts_with',
  GT: 'gt',
  GTE: 'gte',
  LT: 'lt',
  LTE: 'lte',
  EXISTS: 'exists',
  IS_EMPTY: 'is_empty',
} as const;
export type CustomTagOperator = (typeof CustomTagOperator)[keyof typeof CustomTagOperator];

export const CUSTOM_TAG_OPERATOR_LABELS: Record<CustomTagOperator, string> = {
  [CustomTagOperator.EQUALS]: 'equals',
  [CustomTagOperator.NOT_EQUALS]: 'does not equal',
  [CustomTagOperator.IS_ONE_OF]: 'is one of',
  [CustomTagOperator.IS_NOT_ONE_OF]: 'is not one of',
  [CustomTagOperator.CONTAINS]: 'contains',
  [CustomTagOperator.STARTS_WITH]: 'starts with',
  [CustomTagOperator.GT]: '>',
  [CustomTagOperator.GTE]: '>=',
  [CustomTagOperator.LT]: '<',
  [CustomTagOperator.LTE]: '<=',
  [CustomTagOperator.EXISTS]: 'exists',
  [CustomTagOperator.IS_EMPTY]: 'is empty',
} as const;

export type CustomTagOperatorValueShape = 'string' | 'number' | 'string-list' | 'none';

export const CUSTOM_TAG_OPERATOR_VALUE_SHAPE: Record<CustomTagOperator, CustomTagOperatorValueShape> = {
  [CustomTagOperator.EQUALS]: 'string',
  [CustomTagOperator.NOT_EQUALS]: 'string',
  [CustomTagOperator.IS_ONE_OF]: 'string-list',
  [CustomTagOperator.IS_NOT_ONE_OF]: 'string-list',
  [CustomTagOperator.CONTAINS]: 'string',
  [CustomTagOperator.STARTS_WITH]: 'string',
  [CustomTagOperator.GT]: 'number',
  [CustomTagOperator.GTE]: 'number',
  [CustomTagOperator.LT]: 'number',
  [CustomTagOperator.LTE]: 'number',
  [CustomTagOperator.EXISTS]: 'none',
  [CustomTagOperator.IS_EMPTY]: 'none',
} as const;

export type CustomTagTriggerId =
  | 'claim_created'
  | 'claim_approved'
  | 'claim_denied'
  | 'claim_resolution_created'
  | 'refund_issued';

export interface CustomTagTrigger {
  /** Persisted on CustomTagRule.triggerEvent. */
  id: CustomTagTriggerId;
  /** Internal Kafka topic the trigger maps to (or the demuxed source for compound triggers). */
  kafkaTopic: string;
  /** Merchant-facing display label in the trigger dropdown. */
  label: string;
  /** Sub-line under the label describing when the trigger fires. */
  description: string;
  /** Right-aligned event ID shown to power users in the dropdown (dot notation). */
  displayEventId: string;
  /** v1 visibility — `refund_issued` is rendered disabled until its payload is normalized. */
  disabled?: boolean;
}

export const CUSTOM_TAG_TRIGGERS: readonly CustomTagTrigger[] = [
  {
    id: 'claim_created',
    kafkaTopic: 'claim_created',
    label: 'When a claim is filed',
    description: 'Fires the moment a shopper submits a new claim.',
    displayEventId: 'claim.created',
  },
  {
    id: 'claim_approved',
    kafkaTopic: 'claim_state_updated',
    label: 'When a claim is approved',
    description: 'Fires when the OP team approves a claim for resolution.',
    displayEventId: 'claim.approved',
  },
  {
    id: 'claim_denied',
    kafkaTopic: 'claim_state_updated',
    label: 'When a claim is denied',
    description: 'Fires when a claim is rejected.',
    displayEventId: 'claim.denied',
  },
  {
    id: 'claim_resolution_created',
    kafkaTopic: 'claim_resolution_created',
    label: 'When a claim is resolved',
    description: 'Fires once a reship, refund, or store credit is issued.',
    displayEventId: 'claim_resolution.created',
  },
  {
    id: 'refund_issued',
    kafkaTopic: 'refund_issued',
    label: 'When a refund is issued',
    description: 'Fires when money is returned to the customer.',
    displayEventId: 'refund.issued',
    disabled: true,
  },
] as const;

export type CustomTagVariableGroup = 'CLAIM' | 'RESOLUTION' | 'ORDER' | 'CUSTOMER' | 'ORDER_LINE_ITEM';

/** Fields shared by every variable in the catalog. */
type CustomTagVariableBase = {
  /** Path inserted into the template (e.g. `{{line_items[0].id}}` -> `line_items[0].id`). */
  path: string;
  /** Merchant-facing chip label. */
  label: string;
  /** Group header in the chip picker. */
  group: CustomTagVariableGroup;
};

/**
 * Discriminated union so the type system enforces that every
 * `type: 'enum'` entry MUST also declare its `enumValues`. A bare
 * `type: 'enum'` (no enumValues) would otherwise compile fine and
 * silently render an empty dropdown.
 *
 * `enumValues` matches what `displayValue()` produces in
 * `apps/claims-svc/src/claims/tag-context.service.ts` — the strip of
 * `OP:` and `Approved-` prefixes from the canonical label table at
 * `libs/shared/nest/constants/src/labels.constants.ts`. Source of
 * truth is the label table; this list duplicates it for FE rendering.
 * If the label table changes, update this list too (the strip logic
 * does NOT vary, so the transformation is stable).
 */
export type CustomTagVariable =
  | (CustomTagVariableBase & { type: 'string' | 'number' })
  | (CustomTagVariableBase & { type: 'enum'; enumValues: readonly string[] });

export const CUSTOM_TAG_VARIABLE_CATALOG: readonly CustomTagVariable[] = [
  { group: 'CLAIM', path: 'claim.id', label: 'Claim ID', type: 'string' },
  {
    group: 'CLAIM',
    path: 'claim.type',
    label: 'Claim Type',
    type: 'enum',
    enumValues: [
      'OrderDeliveredNotReceived',
      'TrackingNotMoving',
      'MissingItem',
      'Damaged',
      'ReturnToSender',
      'DefectiveItem',
      'WrongItem',
      'Stolen',
      'WrongAddress',
      'MultipleClaimTypes',
    ],
  },
  {
    group: 'CLAIM',
    path: 'claim.category',
    label: 'Claim Category',
    type: 'enum',
    enumValues: ['ShippingClaim', 'WarrantyClaim'],
  },
  {
    group: 'CLAIM',
    path: 'claim.state',
    label: 'Claim State',
    type: 'enum',
    // Only `InProgress`, `Approved`, `Denied` are reachable in v1 — those
    // are the states the four wired triggers (claim_created /
    // claim_approved / claim_denied / claim_resolution_created) leave the
    // claim in at the moment the event fires. The other values are
    // listed for completeness so a rule's stored value still validates
    // against the catalog, but they will never match in practice.
    enumValues: ['InProgress', 'Approved', 'Denied', 'WaitingForCustomer', 'NeedsAssistance', 'Closed'],
  },

  {
    group: 'RESOLUTION',
    path: 'resolution.type',
    label: 'Resolution Type',
    type: 'enum',
    enumValues: ['Refund', 'Reship', 'StoreCredit', 'FlatRate', 'Custom'],
  },
  { group: 'RESOLUTION', path: 'resolution.amount', label: 'Resolution Amount', type: 'number' },
  { group: 'RESOLUTION', path: 'resolution.currency', label: 'Currency', type: 'string' },

  { group: 'ORDER', path: 'order.id', label: 'Order ID', type: 'string' },
  { group: 'ORDER', path: 'order.number', label: 'Order Number', type: 'string' },
  { group: 'ORDER', path: 'order.name', label: 'Order Name', type: 'string' },

  { group: 'CUSTOMER', path: 'customer.first_name', label: 'First Name', type: 'string' },
  { group: 'CUSTOMER', path: 'customer.last_name', label: 'Last Name', type: 'string' },
  { group: 'CUSTOMER', path: 'customer.email', label: 'Email', type: 'string' },
  { group: 'CUSTOMER', path: 'customer.claims_count', label: 'Claims (Total)', type: 'number' },

  // v1: line-item chips hardcode [0] — per-item iteration deferred.
  // `line_items[0].id` returns the Shopify line item ID (from
  // `orderItems[*].sourceItemId`), not the internal OP cuid — matches
  // what merchants see in Shopify admin and is more useful in templates.
  { group: 'ORDER_LINE_ITEM', path: 'line_items[0].id', label: 'Order Line Item ID', type: 'string' },
  { group: 'ORDER_LINE_ITEM', path: 'line_items[0].quantity', label: 'Order Line Item Qty', type: 'number' },
  { group: 'ORDER_LINE_ITEM', path: 'line_items[0].sku', label: 'SKU', type: 'string' },
  { group: 'ORDER_LINE_ITEM', path: 'line_items[0].name', label: 'Name', type: 'string' },
] as const;

/**
 * O(1) lookup from variable path → allowed enum values. Precomputed
 * once at module load so the FE condition row doesn't `Array.find`
 * over the catalog twice per render (once for the `v-if` gate, once
 * for the `<option v-for>` list). Returns `undefined` for paths that
 * aren't enum-typed, which is the signal the FE uses to render the
 * free-text input instead of a dropdown.
 */
const ENUM_VALUES_BY_PATH = new Map<string, readonly string[]>(
  CUSTOM_TAG_VARIABLE_CATALOG.filter(
    (v): v is Extract<CustomTagVariable, { type: 'enum' }> => v.type === 'enum',
  ).map(v => [v.path, v.enumValues]),
);

export function enumValuesForPath(path: string | undefined | null): readonly string[] | undefined {
  if (!path) return undefined;
  return ENUM_VALUES_BY_PATH.get(path);
}

export const CUSTOM_TAG_VARIABLE_GROUP_LABELS: Record<CustomTagVariableGroup, string> = {
  CLAIM: 'Claim',
  RESOLUTION: 'Resolution',
  ORDER: 'Order',
  CUSTOMER: 'Customer',
  ORDER_LINE_ITEM: 'Order Line Item',
} as const;

/**
 * Per-trigger variable visibility. `claim_created` fires before any
 * resolution exists, so the Resolution group is hidden from the chip
 * picker for that trigger. Same shape extends naturally as new triggers
 * land in v2 (e.g. `refund_issued`).
 */
export const VARIABLES_AVAILABLE_BY_TRIGGER: Record<CustomTagTriggerId, readonly CustomTagVariableGroup[]> = {
  claim_created: ['CLAIM', 'ORDER', 'CUSTOMER', 'ORDER_LINE_ITEM'],
  claim_approved: ['CLAIM', 'RESOLUTION', 'ORDER', 'CUSTOMER', 'ORDER_LINE_ITEM'],
  claim_denied: ['CLAIM', 'ORDER', 'CUSTOMER', 'ORDER_LINE_ITEM'],
  claim_resolution_created: ['CLAIM', 'RESOLUTION', 'ORDER', 'CUSTOMER', 'ORDER_LINE_ITEM'],
  refund_issued: ['CLAIM', 'RESOLUTION', 'ORDER', 'CUSTOMER', 'ORDER_LINE_ITEM'],
} as const;

/**
 * Sentinel value used by the custom-tag emit path. Lets shopify-svc tell
 * custom rules apart from the system-tag pipeline and skips the
 * `syncSettings.syncWithPlatform` / `isBlockedTagSync` filters that the
 * system flow uses inside `alertLabelUpdated`.
 */
export const CUSTOM_TAG_SOURCE = 'custom_rule';
